// src/components/managed-sources/GoogleAdsAuthStep.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandGoogle,
  IconExternalLink,
  IconPlugConnected,
  IconX,
} from "@tabler/icons-react";

import {
  getGoogleAuthUrl,
  exchangeGoogleToken,
  listGoogleAdsCustomers,
} from "@/api/managedSources";
import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  authorizationRenderOption,
  authorizationSelectData,
  isUsableByMe,
} from "@/components/authorizations/authorizationOwnership";
import type { GoogleAdsCustomer } from "@/types/managedSources";

type Props = {
  authorizationId: string | null;
  customerId: string;
  onAuthorizationChange: (id: string | null) => void;
  /**
   * Reports BOTH ids together, deliberately. `login_customer_id` is the manager
   * the request is authorised THROUGH; for an account reached via an MCC, Google
   * evaluates authorisation against it and returns USER_PERMISSION_DENIED when
   * it is absent. The picker knew the manager all along and dropped it here, so
   * exactly the accounts the manager grouping exists for could not be connected.
   *
   * One callback carrying both rather than two callbacks: a second, parallel
   * setter is free to drift out of sync with the first, which is the shape of
   * the original bug.
   */
  onCustomerIdChange: (id: string, loginCustomerId: string) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
};

/**
 * Group the account picker by manager, the way Google's own account chooser does.
 *
 * Two API facts drive this, both verified against the live API rather than
 * assumed:
 *
 *  - `listAccessibleCustomers` returns only accounts the user is linked to
 *    DIRECTLY, and in no documented order. In the common agency setup the user
 *    is linked to the manager alone, so that endpoint on its own yields a picker
 *    where every row is a manager — none of them selectable, because a manager
 *    holds no campaigns. The backend therefore expands each manager through
 *    `customer_client` and stamps `manager_customer_id` on what it finds.
 *  - `customer_client.status` is real and matters: a live account tree contained
 *    a CLOSED account. Offering it would let someone build a pipeline on an
 *    account that can never produce data.
 *
 * Managers become GROUP HEADINGS rather than disabled rows. A heading says
 * "these belong together" without pretending to be a choice, which a greyed-out
 * entry does not.
 */
export function buildAccountOptions(customers: GoogleAdsCustomer[]) {
  const label = (c: GoogleAdsCustomer) => {
    const name = c.descriptive_name
      ? `${c.descriptive_name} (${c.customer_id_formatted})`
      : // An empty descriptive_name is normal in Google Ads, not an error — say
        // so rather than showing a bare number beside named siblings.
        `Unnamed account (${c.customer_id_formatted})`;
    // Status is part of the label, not a hidden reason: an account someone is
    // looking for should be visible with an explanation, never silently absent.
    return c.status && c.status !== "ENABLED"
      ? `${name} — ${c.status.toLowerCase()}`
      : name;
  };

  const selectable = (c: GoogleAdsCustomer) =>
    c.is_manager !== true && (!c.status || c.status === "ENABLED");

  const byLabel = (a: GoogleAdsCustomer, b: GoogleAdsCustomer) =>
    label(a).localeCompare(label(b), undefined, { sensitivity: "base" });

  const toOption = (c: GoogleAdsCustomer) => ({
    value: c.customer_id_formatted,
    label: label(c),
    disabled: !selectable(c),
  });

  const managers = new Map(
    customers.filter((c) => c.is_manager === true).map((c) => [c.customer_id, c]),
  );

  const groups: { group: string; items: ReturnType<typeof toOption>[] }[] = [];

  // One group per manager, in name order, holding everything beneath it.
  const orderedManagers = [...managers.values()].sort(byLabel);
  for (const m of orderedManagers) {
    const children = customers
      .filter((c) => c.manager_customer_id === m.customer_id)
      .sort(byLabel);
    if (children.length === 0) continue; // an empty heading explains nothing
    groups.push({
      group: m.descriptive_name
        ? `${m.descriptive_name} (${m.customer_id_formatted})`
        : `Manager ${m.customer_id_formatted}`,
      items: children.map(toOption),
    });
  }

  // Accounts with no manager above them — directly linked standalones.
  const standalone = customers
    .filter((c) => c.is_manager !== true && !c.manager_customer_id)
    .sort(byLabel);
  if (standalone.length > 0) {
    groups.push({
      group: orderedManagers.length > 0 ? "Not under a manager" : "Ad accounts",
      items: standalone.map(toOption),
    });
  }

  // Finally: any group with nothing pickable in it sinks to the bottom. A
  // manager whose only account is closed is still worth showing — someone
  // looking for that account needs to see why it is unavailable — but it must
  // not sit above the accounts that can actually be chosen. Stable, so the
  // ordering above (managers by name, then standalones) survives within each
  // half.
  const hasSelectable = (g: { items: { disabled: boolean }[] }) =>
    g.items.some((o) => !o.disabled);
  return [...groups.filter(hasSelectable), ...groups.filter((g) => !hasSelectable(g))];
}

/**
 * The manager id an account must be addressed through, or "" when there is none.
 *
 * "" is meaningful and must never become a platform-wide fallback: sending
 * Verity's own manager id would have Google evaluate authorisation against OUR
 * link instead of the tenant's — a cross-tenant hole. The backend documents the
 * same rule at the other end.
 */
export function resolveLoginCustomerId(
  customers: GoogleAdsCustomer[],
  selectedFormattedId: string,
): string {
  const match = customers.find(
    (c) => c.customer_id_formatted === selectedFormattedId,
  );
  return match?.manager_customer_id || "";
}

/** Validate and format customer ID: 123-456-7890 */
function formatCustomerId(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function isValidCustomerId(value: string): boolean {
  return /^\d{3}-\d{3}-\d{4}$/.test(value);
}

export default function GoogleAdsAuthStep({
  authorizationId,
  customerId,
  onAuthorizationChange,
  onCustomerIdChange,
  onConnectionNameChange,
  onValidated,
}: Props) {
  // Existing authorizations
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Mode
  const [mode, setMode] = useState<"existing" | "new">("existing");

  // New auth form
  const [authName, setAuthName] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState("");

  // Customer accounts
  const [customers, setCustomers] = useState<GoogleAdsCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState("");

  // OAuth popup message listener ref
  const popupRef = useRef<Window | null>(null);
  const exchangedRef = useRef(false);

  // Load existing Google authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const auths = await listAuthorizations({ type: "GOOGLE" });
        setExistingAuths(auths);
        // Only rows I may use count (ADR-0329): a list of colleagues'
        // personal connections alone starts on "Create new connection".
        if (!auths.some(isUsableByMe)) {
          setMode("new");
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Validate when authorization + customer_id are set
  useEffect(() => {
    onValidated(!!authorizationId && isValidCustomerId(customerId));
  }, [authorizationId, customerId, onValidated]);

  // Load customer accounts when authorization is set
  useEffect(() => {
    if (!authorizationId) {
      setCustomers([]);
      return;
    }
    async function loadCustomers() {
      setLoadingCustomers(true);
      setCustomerError("");
      try {
        const result = await listGoogleAdsCustomers(authorizationId!);
        setCustomers(result.customers);
      } catch (e: any) {
        setCustomerError(e?.message || "Failed to load Google Ads accounts");
      } finally {
        setLoadingCustomers(false);
      }
    }
    loadCustomers();
  }, [authorizationId]);

  // Listen for OAuth popup messages
  const handleOAuthMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "google_oauth_callback") return;

      const { code, error } = event.data;
      popupRef.current = null;

      if (error) {
        setOauthError(`Google authorization failed: ${error}`);
        setOauthLoading(false);
        return;
      }

      if (!code) {
        setOauthError("No authorization code received from Google.");
        setOauthLoading(false);
        return;
      }

      // Prevent duplicate exchange calls
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      try {
        const result = await exchangeGoogleToken(
          code,
          authName.trim(),
          "GOOGLE_ADS",
        );
        onAuthorizationChange(result.authorization_id);
      } catch (e: any) {
        setOauthError(e?.message || "Failed to exchange authorization code");
      } finally {
        setOauthLoading(false);
      }
    },
    [authName, onAuthorizationChange],
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage);
    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [handleOAuthMessage]);

  async function handleConnectGoogle() {
    if (!authName.trim()) return;
    exchangedRef.current = false;
    setOauthLoading(true);
    setOauthError("");

    try {
      const { url } = await getGoogleAuthUrl(undefined, "GOOGLE_ADS");
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "google_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      const checkInterval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkInterval);
          if (oauthLoading) {
            setOauthLoading(false);
          }
        }
      }, 1000);
    } catch (e: any) {
      setOauthError(e?.message || "Failed to get authorization URL");
      setOauthLoading(false);
    }
  }

  function handleSelectExistingAuth(authId: string | null) {
    onAuthorizationChange(authId);
  }

  const existingAuthOptions = authorizationSelectData(existingAuths);

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconBrandGoogle size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Google Ads
          </Text>
          <Text size="sm" c="dimmed">
            Link your Google Ads account to sync campaign data via BigQuery Data
            Transfer Service.
          </Text>
        </Stack>
      </Group>

      {/* Mode selector */}
      {existingAuths.length > 0 && (
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as "existing" | "new");
            if (v === "new") {
              onAuthorizationChange(null);
            }
          }}
          data={[
            { label: "Use existing connection", value: "existing" },
            { label: "Create new connection", value: "new" },
          ]}
          fullWidth
        />
      )}

      {/* Existing authorization */}
      {mode === "existing" && existingAuths.length > 0 && (
        <Stack gap="md">
          <Select
            label="Google authorization"
            placeholder="Select a saved connection"
            data={existingAuthOptions}
            renderOption={authorizationRenderOption(existingAuths)}
            value={authorizationId}
            onChange={handleSelectExistingAuth}
            size="md"
          />
        </Stack>
      )}

      {/* New authorization */}
      {mode === "new" && (
        <Stack gap="md">
          <TextInput
            label="Connection name"
            placeholder="e.g. My Google Ads"
            value={authName}
            onChange={(e) => {
              setAuthName(e.currentTarget.value);
              onConnectionNameChange?.(e.currentTarget.value);
            }}
            required
            size="md"
            disabled={!!authorizationId}
          />

          {!authorizationId && (
            <Button
              variant="outline"
              leftSection={<IconBrandGoogle size={18} />}
              rightSection={<IconExternalLink size={14} />}
              onClick={handleConnectGoogle}
              loading={oauthLoading}
              disabled={!authName.trim()}
              size="md"
              fullWidth
            >
              Sign in with Google
            </Button>
          )}

          {oauthError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {oauthError}
            </Alert>
          )}
        </Stack>
      )}

      {/* Customer account selector (shown after auth) */}
      {authorizationId && (
        <>
          {loadingCustomers ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Loading Google Ads accounts...
              </Text>
            </Group>
          ) : customerError ? (
            <Alert color="red" icon={<IconX size={16} />}>
              {customerError}
            </Alert>
          ) : customers.length > 0 ? (
            <Select
              label="Google Ads account"
              placeholder="Select an account"
              data={buildAccountOptions(customers)}
              value={customerId || null}
              onChange={(v) =>
                onCustomerIdChange(v || "", resolveLoginCustomerId(customers, v || ""))
              }
              size="md"
              searchable
            />
          ) : (
            <TextInput
              label="Google Ads Customer ID"
              placeholder="123-456-7890"
              description="No accounts found automatically. Enter the customer ID manually."
              value={customerId}
              onChange={(e) =>
                // Manually typed: no picker context, so no manager is known.
                // Empty is correct here — see resolveLoginCustomerId.
                onCustomerIdChange(formatCustomerId(e.currentTarget.value), "")
              }
              error={
                customerId && !isValidCustomerId(customerId)
                  ? "Format: 123-456-7890"
                  : undefined
              }
              size="md"
              maxLength={12}
            />
          )}
        </>
      )}

      {/* Connected confirmation */}
      {authorizationId && isValidCustomerId(customerId) && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                Customer ID: {customerId}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
